import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { Link, useNavigate } from "react-router-dom";
import { Sparkles, Coins, LogOut } from "lucide-react";

export default function UserDashboard() {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => { api.get("/me/points").then((r) => setData(r.data)); }, []);

  return (
    <div data-testid="user-dashboard" className="container-refined py-16 md:py-24">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div>
          <p className="eyebrow">{t("dashboard.welcome")}</p>
          <h1 className="mt-4 font-serif text-5xl md:text-6xl leading-[1.05] text-forest-900">{user?.name}</h1>
          <p className="mt-3 text-forest-700">{user?.email}</p>
        </div>
        <div className="flex gap-3">
          {user?.role === "admin" && (
            <Link to="/admin" className="btn-secondary" data-testid="dashboard-admin-shortcut">{t("dashboard.admin_shortcut")}</Link>
          )}
          <button data-testid="dashboard-logout" onClick={async () => { await logout(); navigate("/"); }} className="btn-primary">
            <LogOut className="h-4 w-4" strokeWidth={1.5} /> {t("dashboard.logout")}
          </button>
        </div>
      </div>

      <div className="mt-14 grid lg:grid-cols-3 gap-6">
        <div className="card-warm lg:col-span-1" data-testid="dashboard-points-card">
          <p className="eyebrow"><Coins className="inline h-3.5 w-3.5 mr-1" strokeWidth={1.5}/> {t("dashboard.points")}</p>
          <p className="mt-4 font-serif text-6xl text-wheat-700">{data?.points_total ?? 0}</p>
          <p className="mt-2 text-xs text-forest-700/80">pts — for future rewards</p>
          <Link to="/future-interest" className="btn-secondary mt-8" data-testid="dashboard-add-interest">
            <Sparkles className="h-4 w-4" strokeWidth={1.5}/> Earn more
          </Link>
        </div>
        <div className="card-warm lg:col-span-2">
          <p className="eyebrow">{t("dashboard.events")}</p>
          <div className="mt-6 divide-y divide-forest-900/10">
            {(data?.events || []).map((e) => (
              <div key={e.id} className="flex justify-between py-3 text-sm" data-testid={`points-event-${e.id}`}>
                <div>
                  <p className="text-forest-900">{e.reason}</p>
                  <p className="text-xs text-forest-700/80">{new Date(e.created_at).toLocaleString()}</p>
                </div>
                <span className={`font-semibold ${e.delta >= 0 ? "text-forest-800" : "text-destructive"}`}>{e.delta >= 0 ? "+" : ""}{e.delta}</span>
              </div>
            ))}
            {(!data?.events || data.events.length === 0) && <p className="text-forest-700 text-sm">No history yet.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
